import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "@/lib/site";
import SEO from "@/components/SEO";

const LAST_UPDATED = "December 2025";

const Section = ({ n, title, children }) => (
  <section className="border-t border-zinc-200 pt-10 mt-10 first:border-t-0 first:pt-0 first:mt-0">
    <div className="tech-label text-[#C2410C]">// {n}</div>
    <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mt-3 leading-tight">
      {title}
    </h2>
    <div className="mt-6 text-zinc-700 text-base leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const CookieRow = ({ name, provider, purpose, duration, category }) => (
  <tr className="border-b border-zinc-200">
    <td className="py-3 pr-4 align-top text-sm font-mono text-zinc-900">{name}</td>
    <td className="py-3 pr-4 align-top text-sm text-zinc-700">{provider}</td>
    <td className="py-3 pr-4 align-top text-sm text-zinc-700">{purpose}</td>
    <td className="py-3 pr-4 align-top text-sm text-zinc-700 whitespace-nowrap">{duration}</td>
    <td className="py-3 align-top text-[11px] font-mono uppercase tracking-[0.16em] text-[#C2410C]">{category}</td>
  </tr>
);

export default function Cookies() {
  const openPreferences = () => {
    window.dispatchEvent(new Event("open-cookie-preferences"));
  };

  return (
    <div data-testid="cookies-page">
      <SEO
        title="Cookie Policy"
        description="Cookies used on the Concrete Insight UK website and how to control them. UK GDPR and PECR compliant."
      />

      <section className="border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-20">
          <div className="tech-label">// Legal, Cookie Policy</div>
          <h1 className="hero-heading text-4xl sm:text-5xl md:text-6xl mt-6">
            Cookie Policy
          </h1>
          <div className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            Last updated: {LAST_UPDATED}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <Section n="01" title="What cookies are">
          <p>
            Cookies are small text files stored on your device by websites you visit. They are widely used to make websites work, or work more efficiently, and to provide information to the site owner.
          </p>
        </Section>

        <Section n="02" title="How we use cookies">
          <p>
            We keep our cookie use minimal. We only load non-essential cookies (analytics, advertising measurement) after you have given consent through the cookie banner shown on your first visit.
          </p>
          <p>You can change your choice at any time:</p>
          <button
            onClick={openPreferences}
            className="btn-outline"
            data-testid="cookies-open-preferences"
          >
            Manage cookie preferences
          </button>
        </Section>

        <Section n="03" title="Cookies we may use">
          <div className="overflow-x-auto -mx-6 md:mx-0">
            <table className="w-full min-w-[720px] border-t border-zinc-200 mt-2">
              <thead>
                <tr className="text-left border-b border-zinc-300">
                  <th className="py-3 pr-4 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-500">Name</th>
                  <th className="py-3 pr-4 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-500">Provider</th>
                  <th className="py-3 pr-4 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-500">Purpose</th>
                  <th className="py-3 pr-4 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-500">Duration</th>
                  <th className="py-3 text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-500">Category</th>
                </tr>
              </thead>
              <tbody>
                <CookieRow
                  name="ci_consent"
                  provider="concreteinsightuk.co.uk"
                  purpose="Remembers your cookie preferences so the banner is not shown on every visit."
                  duration="12 months"
                  category="Necessary"
                />
                <CookieRow
                  name="_ga, _ga_*"
                  provider="Google Analytics"
                  purpose="Distinguishes unique visitors and measures how the site is used. Only set with consent."
                  duration="2 years"
                  category="Analytics"
                />
                <CookieRow
                  name="_gid"
                  provider="Google Analytics"
                  purpose="Distinguishes users within a 24-hour period. Only set with consent."
                  duration="24 hours"
                  category="Analytics"
                />
                <CookieRow
                  name="_gcl_au"
                  provider="Google Ads"
                  purpose="Measures conversions from Google Ads campaigns. Only set with consent."
                  duration="90 days"
                  category="Marketing"
                />
              </tbody>
            </table>
          </div>
        </Section>

        <Section n="04" title="Third-party sites">
          <p>
            Some content on our site (Google Fonts, illustrations from Fosroc, our own hosted images) is loaded from third-party servers. Those providers may set their own technical cookies purely to serve the content. We do not control those cookies. If you have concerns, most modern browsers allow you to block third-party cookies entirely.
          </p>
        </Section>

        <Section n="05" title="Controlling cookies in your browser">
          <p>You can also control cookies directly in your browser settings:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Chrome: Settings, Privacy and security, Cookies</li>
            <li>Safari: Preferences, Privacy</li>
            <li>Firefox: Preferences, Privacy &amp; Security</li>
            <li>Edge: Settings, Cookies and site permissions</li>
          </ul>
        </Section>

        <Section n="06" title="More information">
          <p>
            For more on how we handle personal data see our{" "}
            <Link to="/privacy" className="text-[#C2410C] underline">
              Privacy Policy
            </Link>. For any questions email{" "}
            <a href={`mailto:${BRAND.email}`} className="text-[#C2410C] underline">
              {BRAND.email}
            </a>.
          </p>
        </Section>
      </div>
    </div>
  );
}
